import { Dayjs } from 'dayjs'

/**
 * Anything time-versioned by a half-open validity window. Both bounds are optional so this fits
 * timelines whose generated types differ — remuneration records always carry a `validFrom`,
 * pay classification records do not.
 */
export type TimeBoundedRecord = {
  validFrom?: string
  validTo?: string
}

/**
 * Finds the record whose `[validFrom, validTo)` window contains the reference date. Bounds are
 * compared as `YYYY-MM-DD` strings, and a missing bound means unbounded on that side.
 *
 * Half-open matters: a record's `validTo` equals its successor's `validFrom`, so an inclusive
 * upper bound would match two records on a boundary date.
 */
export const findRecordCoveringDate = <T extends TimeBoundedRecord>(
  records: T[] | undefined,
  referenceDate: Dayjs
): T | undefined => {
  if (!records) return undefined

  const refDateString = referenceDate.format('YYYY-MM-DD')

  return records.find((record) => {
    const isAfterValidFrom = record.validFrom
      ? refDateString >= record.validFrom
      : true
    const isBeforeValidTo = record.validTo
      ? refDateString < record.validTo
      : true

    return isAfterValidFrom && isBeforeValidTo
  })
}
